import { Injectable } from '@angular/core';
import { MachineChartData, ProductionOrderData2, ProductionPeriodDetailData, WasteAssignmentInTime, WastePerReason, WorkCenterTask } from 'chronos-core-client';
import { TelemetryResultDto, TelemetryValueDto } from 'chronos-panda-client';
import moment from 'moment-mini';
import { ChartType } from '../models/chart-type.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { SignalType } from './machine-data';
import { EmptyTextPlaceholderPipe } from '../pipes/empty-text-placeholder/empty-text-placeholder.pipe';
import { FormatQuantityPipe } from '../pipes/format-quantity/format-quantity.pipe';
import { FilterAlgorithmService } from './filter-algorithm-service';
import { Point, PointOptionsObject } from 'highcharts';
import { DecimalPipe } from '@angular/common';
import { DisplayWindow } from './display-window';

@Injectable({
  providedIn: 'root'
})
export class MachineChartConfigurationService {
  public runModeText: string;
  public targetSpeedText: string;
  public setupModeText: string;
  public downModeText: string;
  public downtimeCodeText: string;
  public downtimeCommentText: string;
  public noCommentText: string;
  public startText: string;
  public remainingTimeText: string;
  public shtCounterText: string;
  public pcsCounterText: string;
  public shiftText: string;
  public externalShiftTypeText: string;
  public dateText: string;
  public productionOrderNumberText: string;
  public finishGoodArticleNumberText: string;
  public finishGoodArticleNameText: string;
  public configNameNumberText: string;
  public goodQuantityUnitText: string;
  public wasteQuantMalQuntText: string;
  public dateFormatText: string;
  public speed2UnitText: string;
  public productionChangeSeries: string;
  public speedSeriesText: string;
  public speed2SeriesText: string;
  public washSeriesText: string;
  public downtimeTaskText: string;
  public newSeriesName: string;
  public wasteQuantityText: string;
  public maculatureQuantityText: string;
  public wasteReasonText: string;
  public setupKindText: string;
  public errorSeriesText: string;

  private fontFamily = 'Inter, Segoe UI, sans-serif';
  private readonly turboThreshold = 20000;

  constructor(
    private translate: TranslateService,
    private formatQuantityPipe: FormatQuantityPipe,
    private emptyTextPlaceholderPipe: EmptyTextPlaceholderPipe,
    private decimalPipe: DecimalPipe) {
    this.setChartTranslations();
  }

  public setChartTranslations() {
    this.translate
      .stream([
        'MACHINE_CHART.RUN_MODE_TEXT',
        'MACHINE_CHART.TARGET_SPEED_TEXT',
        'MACHINE_CHART.SETUP_MODE_TEXT',
        'MACHINE_CHART.DOWN_MODE_TEXT',
        'MACHINE_CHART.DOWNTIME_CODE_TEXT',
        'MACHINE_CHART.DOWNTIME_COMMENT_TEXT',
        'MACHINE_CHART.NO_COMMENT',
        'MACHINE_CHART.START',
        'MACHINE_CHART.REST',
        'MACHINE_CHART.SHT_UNIT',
        'MACHINE_CHART.PCS_UNIT',
        'MACHINE_CHART.SHIFT',
        'MACHINE_CHART.EXTERNAL_SHIFT_TYPE',
        'MACHINE_CHART.DATE',
        'MACHINE_CHART.PRODUCTION_ORDER_NUMBER',
        'MACHINE_CHART.FINISH_GOOD_ARTICLE_NUMBER',
        'MACHINE_CHART.FINISH_GOOD_ARTICLE_NAME',
        'MACHINE_CHART.CONFIGURATION_NUMBER_AND_NAME',
        'MACHINE_CHART.GOOD_QUANITY_UNIT',
        'MACHINE_CHART.WASTE_QUANTITY_MACULATURE_QUANTITY',
        'MACHINE_CHART.DATE_FORMAT',
        'MACHINE_CHART.SPEED_SERIES',
        'MACHINE_CHART.SPEED2_UNIT',
        'MACHINE_CHART.SPEED2_SERIES',
        'MACHINE_CHART.WASH_SERIES',
        'MACHINE_CHART.DOWNTIME_TASK',
        'MACHINE_CHART.WASTE_QUANTITY',
        'MACHINE_CHART.MACULATURE_QUANTITY',
        'MACHINE_CHART.WASTE_REASON',
        'MACHINE_CHART.SETUP_KIND',
        'MACHINE_CHART.ERROR_SERIES_TEXT'
      ])
      .subscribe((values) => {
        Object.keys(values).map((key) => {
          switch (key) {
            case 'MACHINE_CHART.SPEED_SERIES':
              this.speedSeriesText = values[key];
              break;
            case 'MACHINE_CHART.SPEED2_SERIES':
              this.speed2SeriesText = values[key];
              break;
            case 'MACHINE_CHART.WASH_SERIES':
              this.washSeriesText = values[key];
              break;
            case 'MACHINE_CHART.RUN_MODE_TEXT':
              this.runModeText = values[key];
              break;
            case 'MACHINE_CHART.TARGET_SPEED_TEXT':
              this.targetSpeedText = values[key];
              break;
            case 'MACHINE_CHART.SETUP_MODE_TEXT':
              this.setupModeText = values[key];
              break;
            case 'MACHINE_CHART.DOWN_MODE_TEXT':
              this.downModeText = values[key];
              break;
            case 'MACHINE_CHART.DOWNTIME_CODE_TEXT':
              this.downtimeCodeText = values[key];
              break;
            case 'MACHINE_CHART.DOWNTIME_COMMENT_TEXT':
              this.downtimeCommentText = values[key];
              break;
            case 'MACHINE_CHART.NO_COMMENT':
              this.noCommentText = values[key];
              break;
            case 'MACHINE_CHART.START':
              this.startText = values[key];
              break;
            case 'MACHINE_CHART.REST':
              this.remainingTimeText = values[key];
              break;
            case 'MACHINE_CHART.SHT_UNIT':
              this.shtCounterText = values[key];
              break;
            case 'MACHINE_CHART.PCS_UNIT':
              this.pcsCounterText = values[key];
              break;
            case 'MACHINE_CHART.SHIFT':
              this.shiftText = values[key];
              break;
            case 'MACHINE_CHART.EXTERNAL_SHIFT_TYPE':
              this.externalShiftTypeText = values[key];
              break;
            case 'MACHINE_CHART.DATE':
              this.dateText = values[key];
              break;
            case 'MACHINE_CHART.PRODUCTION_ORDER_NUMBER':
              this.productionOrderNumberText = values[key];
              break;
            case 'MACHINE_CHART.FINISH_GOOD_ARTICLE_NUMBER':
              this.finishGoodArticleNumberText = values[key];
              break;
            case 'MACHINE_CHART.FINISH_GOOD_ARTICLE_NAME':
              this.finishGoodArticleNameText = values[key];
              break;
            case 'MACHINE_CHART.CONFIGURATION_NUMBER_AND_NAME':
              this.configNameNumberText = values[key];
              break;
            case 'MACHINE_CHART.GOOD_QUANITY_UNIT':
              this.goodQuantityUnitText = values[key];
              break;
            case 'MACHINE_CHART.WASTE_QUANTITY_MACULATURE_QUANTITY':
              this.wasteQuantMalQuntText = values[key];
              break;
            case 'MACHINE_CHART.DATE_FORMAT':
              this.dateFormatText = values[key];
              break;
            case 'MACHINE_CHART.SPEED2_UNIT':
              this.speed2UnitText = values[key];
              break;
            case 'MACHINE_CHART.DOWNTIME_TASK':
              this.downtimeTaskText = values[key];
              break;
            case 'MACHINE_CHART.WASTE_QUANTITY':
              this.wasteQuantityText = values[key];
              break;
            case 'MACHINE_CHART.MACULATURE_QUANTITY':
              this.maculatureQuantityText = values[key];
              break;
            case 'MACHINE_CHART.WASTE_REASON':
              this.wasteReasonText = values[key];
              break;
            case 'MACHINE_CHART.SETUP_KIND':
              this.setupKindText = values[key];
              break;
            case 'MACHINE_CHART.ERROR_SERIES_TEXT':
              this.errorSeriesText = values[key];
              break;
          }
        });
      });
  }

  public setChartTitle(
    chart: Highcharts.Options,
    titleText: string
  ) : void {
    // when the configuration has no title, do nothing
    if (!chart.title) {
      return;
    }
    if (!titleText) {
      return;
    }
    chart.title.text = titleText;
  }

  public setPrimaryYAxisTitle(
      chartDetails: Highcharts.Options,
      counterUnitId: string
  ) : void {
    if (chartDetails.yAxis && chartDetails.yAxis[0]) {
      chartDetails.yAxis[0].title = {
        text: this.getSeriesTitleText(counterUnitId)
      };
    }
  }

  public static getPlotBandSeries(chartType: ChartType, series: Highcharts.Series[]): Highcharts.Series {
    if (chartType !== ChartType.editDowntime && chartType !== ChartType.editSetup) {
      throw new Error('Invalid chart type for getPlotBandSeries');
    }
    return series[5];
  }

  public static getPointEditSeries(chartType: ChartType, series: Highcharts.Series[]): Highcharts.Series {
    if (chartType !== ChartType.splitDowntime) {
      throw new Error('Invalid chart type for getPlotBandSeries');
    }
    return series[5];
  }

  public static getRangeEditStartSeries(chartType: ChartType, series: Highcharts.Series[]): Highcharts.Series {
    if (chartType !== ChartType.editDowntime && chartType !== ChartType.editSetup) {
      throw new Error('Invalid chart type for getRangeEditStartSeries');
    }
    return series[6];
  }

  public static getRangeEditEndSeries(chartType: ChartType, series: Highcharts.Series[]): Highcharts.Series {
    if (chartType !== ChartType.editDowntime && chartType !== ChartType.editSetup) {
      throw new Error('Invalid chart type for getRangeEditEndSeries');
    }
    return series[7];
  }

  public getSplitOrEditDowntimeChartConfiguration(): Highcharts.Options {
    return {
      chart: {
        height: 400,
        marginTop: 20,
        animation: false,
        zooming: {
          type: 'x',
        }
      },
      title: null,
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      xAxis: [
        {
          id: 'xAxis-1',
          title: {
            text: ''
          },
          type: 'datetime',
          dateTimeLabelFormats: {
            hour: '%H:%M',
            day: ' %k:%M <br> %e %b '
          },
        }
      ],
      yAxis: [
        {
          id: 'yAxis-1',
          title: {
            text: ''
          },
          min: 0,
          max: null,
          startOnTick: false,
          endOnTick: false,
          tickAmount: 9,
        }
      ],
      plotOptions: {
        series: {
          stickyTracking: false,
          dragDrop: {
            draggableX: false,
            groupBy: 'x'
          }
        },
        line: {
          cursor: 'pointer'
        }
      },
      tooltip: {
        borderWidth: 1,
        useHTML: true,
      },
      time: {
        useUTC: false
      },
      series: [],
      exporting: {
        enabled: false
      }
    };
  }

  public getEditSetupChartConfigurationWithoutScroll(): Highcharts.Options {
    return {
      chart: {
        height: 400,
        marginTop: 20,
        animation: false,
        zooming: {
          type: 'x',
        }
      },
      title: null,
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      xAxis: [
        {
          id: 'xAxis-1',
          title: {
            text: ''
          },
          type: 'datetime',
          dateTimeLabelFormats: {
            hour: '%H:%M',
            day: ' %k:%M <br> %e %b '
          },
        },
      ],
      yAxis: [
        {
          id: 'yAxis-1',
          title: {
            text: ''
          },
          min: 0,
          max: null,
          startOnTick: false,
          endOnTick: false,
          tickAmount: 9,
        },
      ],
      plotOptions: {
        series: {
          stickyTracking: false,
          dragDrop: {
            draggableX: false,
            groupBy: 'x'
          }
        },
        line: {
          cursor: 'pointer'
        }
      },
      tooltip: {
        borderWidth: 1,
        useHTML: true,
      },
      time: {
        useUTC: false
      },
      series: [],
      exporting: {
        enabled: false
      }
    };
  }

  public getEditSetupChartConfigurationWithScroll(): Highcharts.Options {
    return {
      chart: {
        height: 400,
        marginTop: 20,
        animation: false,
        zooming: {
          type: 'x',
        }
      },
      title: null,
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      xAxis: [
        {
          id: 'xAxis-1',
          title: {
            text: ''
          },
          type: 'datetime',
          dateTimeLabelFormats: {
            hour: '%H:%M',
            day: ' %k:%M <br> %e %b '
          },
          scrollbar: {
            enabled: true,
            barBackgroundColor: 'gray',
            liveRedraw: false,
            barBorderRadius: 5,
            buttonBorderRadius: 5,
            barBorderWidth: 0,
            rifleColor: 'gray',
            trackBackgroundColor: 'white',
            trackBorderWidth: 1,
            trackBorderColor: 'gray',
            trackBorderRadius: 5
          }
        }
      ],
      yAxis: [
        {
          id: 'yAxis-1',
          title: {
            text: ''
          },
          min: 0,
          max: null,
          startOnTick: false,
          endOnTick: false,
          tickAmount: 9,
        }
      ],
      plotOptions: {
        series: {
          stickyTracking: false,
          dragDrop: {
            draggableX: false,
            groupBy: 'x'
          }
        },
        line: {
          cursor: 'pointer'
        }
      },
      tooltip: {
        borderWidth: 1,
        useHTML: true,
      },
      time: {
        useUTC: false
      },
      series: [],
      exporting: {
        enabled: false
      }
    };
  }

  public getFullViewChartConfiguration(
    chartHeight: number
  ): Highcharts.Options {
    return {
      chart: {
        type: 'area',
        animation: false,
        borderColor: '#E6E6E6',
        borderWidth: 1,
        height: chartHeight,
        style: {
          fontFamily: this.fontFamily
        },
        zooming: {
          type: 'x',
        }
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false
      },
      time: {
        useUTC: false
      },
      xAxis: [
        {
          id: 'xAxis-1',
          type: 'datetime',
          dateTimeLabelFormats: {
            hour: '%H:%M',
            day: ' %k:%M <br> %e %b '
          },
          labels: {
            style: {
              font: `14px ${this.fontFamily}`
            }
          },
        },
      ],
      tooltip: {
        borderWidth: 1,
        useHTML: true,
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: false
          }
        }
      },
      yAxis: [
        {
          id: 'yAxis-1',
          title: {
            text: ''
          },
          min: 0,
          max: null,
          startOnTick: false,
          endOnTick: false,
          tickAmount: 9,
          labels: {
            style: {
              font: `14px ${this.fontFamily}`
            }
          }
        },
        {
          id: 'yAxis-2',
          title: {
            text: this.speed2UnitText
          },
          opposite: true,
          min: 0,
          max: null,
          startOnTick: false,
          endOnTick: false,
          tickAmount: 9,
          showEmpty: false,
          gridLineWidth: 0,
          labels: {
            style: {
              font: `14px ${this.fontFamily}`
            }
          }
        }
      ],
      series: [],
      exporting: {
        enabled: true
      }
    };
  }

  public getFinishingPhaseChartConfiguration(): Highcharts.Options {
    return {
      chart: {
        type: 'area',
        animation: false,
        borderColor: '#E6E6E6',
        borderWidth: 1,
        height: 300,
        style: {
          fontFamily: this.fontFamily
        },
        zooming: {
          type: 'x',
        }
      },
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      title: {
        text: '',
        align: 'left',
        margin: 20
      },
      time: {
        useUTC: false
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: false
          }
        }
      },
      xAxis: [
        {
          id: 'xAxis-1',
          type: 'datetime',
          dateTimeLabelFormats: {
            hour: '%H:%M',
            day: ' %k:%M <br> %e %b '
          },
        }
      ],
      yAxis: {
        id: 'yAxis-1',
        title: {
          text: ''
        },
        min: 0,
        max: null,
        startOnTick: false,
        endOnTick: false,
        tickAmount: 9,
      },
      tooltip: {
        borderWidth: 1,
        useHTML: true,
      },
      series: [],
      exporting: {
        enabled: false
      }
    };
  }

  public getSeriesConfiguration(
    chartMode: string,
    result: MachineChartData,
    allDataSignal: TelemetryResultDto[],
    wasteReasons: WasteAssignmentInTime[],
    maxSpeedAxis1: number,
    setupStartTime: Date,
    setupEndTime: Date,
    allowRangeEditStart: boolean,
    allowRangeEditEnd: boolean,
    isMarkerEnabled: boolean,
    downtimeResult: WorkCenterTask,
    displayWindow: DisplayWindow,
    smoothingAlgorithm: string,
    windowSize: number,
    editMinTime?: Date,
    editMaxTime?: Date
  ): Highcharts.SeriesOptionsType[] {
    const outerThis = this;
    const availableSignalSeries = [];
    const errorSeriesHeight = (20 / 100) * maxSpeedAxis1;
    const setupDowntimeSeriesHeight = (15 / 100) * maxSpeedAxis1;
    const runSeriesHeight = (5 / 100) * maxSpeedAxis1;

    const errorSeries: Highcharts.SeriesAreaOptions = {
      id: 'errorSeries',
      name: 'errorSeries',
      type: 'area',
      data: [],
      step: 'left',
      color: '#E070A4',
      fillColor: '#E38CB3CC',
      lineColor: '#E271A4',
      lineWidth: 1,
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          opacity: 1
        }
      },
      showInLegend: false,
      yAxis: 0,
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          return outerThis.getErrorSeriesTooltipText(this['errorSignalData']);
        }
      }
    };

    const setupSeries: Highcharts.SeriesAreaOptions = {
      id: 'setupSeries',
      name: this.setupModeText,
      type: 'area',
      data: [],
      step: 'left',
      color: '#FAE3B3',
      fillColor: '#FAE3B3CC',
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          opacity: 1
        }
      },
      marker: {
        enabled: isMarkerEnabled
      },
      showInLegend: false,
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          return outerThis.getSetupPhaseTooltipText(this['status']);
        }
      }
    };

    const machineRunSeries: Highcharts.SeriesAreaOptions = {
      id: 'machineRunSeries',
      name: this.runModeText,
      type: 'area',
      data: [],
      step: 'left',
      color: '#00A3A5',
      fillColor: '#00B7B9C3',
      lineColor: '#00A3A5',
      lineWidth: 2,
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          opacity: 1
        }
      },
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          return outerThis.getGenericTooltipText(outerThis, this, false);
        }
      },
      marker: {
        enabled: isMarkerEnabled
      },
      showInLegend: true,
      yAxis: 0
    };

    const downTimeSeries: Highcharts.SeriesAreaOptions = {
      id: 'downTimeSeries',
      name: this.downModeText,
      type: 'area',
      cursor: 'pointer',
      data: [],
      color: '#83D6F4',
      turboThreshold: this.turboThreshold,
      step: 'left',
      states: {
        inactive: {
          opacity: 1
        }
      },
      showInLegend: false,
      allowPointSelect: true,
      marker: {
        enabled: isMarkerEnabled,
        states: {
          select: {
            fillColor: '#B3D6F4'
          }
        }
      },
      dataLabels: [
        {
          enabled: true,
          align: 'center',
          style: {
            textShadow: 'none',
            fontSize: '13px'
          },
          x: 14,
          y: 20,
          useHTML: true,
          formatter() {
            return `<div>${(<any>this.point).externalDowntimeLocalCode ?? ''}</div>`;
          }
        }
      ],
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          return outerThis.getDowntimeTooltipText(this['status']);
        }
      }
    };

    const speedSeries: Highcharts.SeriesAreaOptions = {
      id: 'speedSeries',
      name: this.speedSeriesText,
      type: 'area',
      data: [],
      step: 'left',
      color: '#689576',
      fillColor: '#689576',
      lineColor: '#006E45',
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          opacity: 1
        }
      },
      yAxis: 0,
      marker: {
        enabled: isMarkerEnabled
      },
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          return outerThis.getGenericTooltipText(outerThis, this, true);
        }
      },
    };

    const plotBandSeries: Highcharts.SeriesAreaOptions = {
      id: 'plotBandSeries',
      name: 'plotBandSeries',
      type: 'area',
      data: [],
      color: '#00325C',
      lineColor: '#00325C',
      lineWidth: 3,
      fillColor: {
        pattern: {
          path: {
            d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
            strokeWidth: 1
          },
          width: 10,
          height: 10,
          opacity: 0.4
        }
      },
      fillOpacity: 0.8,
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          opacity: 1
        }
      },
      showInLegend: false,
      tooltip: {},
      marker: {
        enabled: false
      },
      enableMouseTracking: false,
      zones: null,
    };

    const rangeEditStartSeries: Highcharts.SeriesLineOptions = {
      id: 'rangeEditStartSeries',
      name: 'setupStartSeries',
      type: 'line',
      stickyTracking: false,
      dragDrop: {
        draggableX: chartMode === ChartType.editDowntime ? false : allowRangeEditStart,
        groupBy: 'groupId',
        dragMinX: editMinTime ? moment.utc(editMinTime).valueOf() : undefined,
        dragMaxX: editMaxTime ? moment.utc(editMaxTime).valueOf() : undefined
      },
      marker: {
        enabled: isMarkerEnabled
      },
      cursor: chartMode === ChartType.editDowntime ? 'none' : 'pointer',
      data: [
        {
          x: moment.utc(setupStartTime).valueOf(),
          y: maxSpeedAxis1,
          groupId: 'a',
          marker:
            chartMode !== ChartType.editDowntime && allowRangeEditStart
              ? {
                  enabled: true,
                  fillColor: '#00325C',
                  useHTML: true,
                  cursor: 'Pointer',
                  symbol: 'url(/assets/images/phase-setup_arrow_left.png)',
                  className: 'plotline-left',
                  radius: 10,
                  align: 'left'
                }
              : undefined,
          noTooltip: true,
          dataLabels: {
            align: 'left',
            enabled: true,
            useHTML: true,
            formatter() {
              return `<div class="plotline-left-label ${(chartMode !== ChartType.editDowntime && allowRangeEditStart) ? 'plotline-left-label__marker' : ''}">${moment(this.x).format('HH:mm')}</div>`;
            }
          }
        } as Highcharts.PointOptionsObject,
        {
          x: moment.utc(setupStartTime).valueOf(),
          y: 0,
          noTooltip: true,
          groupId: 'a',
          marker: {
            enabled: true,
            radius: 0
          }
        } as Highcharts.PointOptionsObject
      ],
      lineWidth: 3,
      showInLegend: false,
      color: '#00325C',
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          opacity: 1
        }
      },
      zones: null,
    };

    const rangeEditEndSeries: Highcharts.SeriesLineOptions = {
      id: 'rangeEditEndSeries',
      name: 'setupEndSeries',
      type: 'line',
      stickyTracking: false,
      dragDrop: {
        draggableX: allowRangeEditEnd,
        groupBy: 'groupId',
        dragMinX: editMinTime ? moment.utc(editMinTime).valueOf() : undefined,
        dragMaxX: editMaxTime ? moment.utc(editMaxTime).valueOf() : undefined
      },
      marker: {
        enabled: isMarkerEnabled
      },
      cursor: 'Pointer',
      className: 'display-label',
      data: [
        {
          noTooltip: true,
          x: moment.utc(setupEndTime).valueOf(),
          y: maxSpeedAxis1,
          groupId: 'a',
          marker:
            allowRangeEditEnd
            ? {
                enabled: true,
                fillColor: '#00325C',
                useHTML: true,
                cursor: 'Pointer',
                symbol: 'url(/assets/images/phase-setup_arrow_right.png)',
                className: 'plotline-right',
                radius: 10,
                align: 'right'
              }
            : undefined,
          dataLabels: {
            align: 'right',
            enabled: true,
            useHTML: true,
            formatter() {
              return `<div class="plotline-right-label ${allowRangeEditEnd ? 'plotline-right-label__marker' : ''}">${moment(this.x).format('HH:mm')}</div>`;
            }
          }
        } as Highcharts.PointOptionsObject,
        {
          noTooltip: true,
          x: moment.utc(setupEndTime).valueOf(),
          y: 0,
          groupId: 'a',
          marker: {
            enabled: false,
            radius: 0
          }
        } as Highcharts.PointOptionsObject
      ],
      lineWidth: 3,
      showInLegend: false,
      color: '#00325C',
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          opacity: 1
        }
      },
      zones: null,
    };

    const editDowntimeEndSeries: Highcharts.SeriesLineOptions = {
      id: 'editDowntimeEndSeries',
      name: 'enditDowntimeEndSeries',
      type: 'line',
      stickyTracking: false,
      dragDrop: {
        draggableX: true,
        groupBy: 'groupId',
        dragMinX: editMinTime ? moment.utc(editMinTime).valueOf() : undefined,
        dragMaxX: editMaxTime ? moment.utc(editMaxTime).valueOf() : undefined
      },
      marker: {
        enabled: isMarkerEnabled
      },
      cursor: 'Pointer',
      className: 'display-label',
      data: [
        {
          noTooltip: true,
          x: moment.utc(setupEndTime).valueOf(),
          y: maxSpeedAxis1,
          groupId: 'ed',
          marker: {
            enabled: true,
            fillColor: '#00325C',
            useHTML: true,
            cursor: 'Pointer',
            symbol: 'url(/assets/images/phase-setup_arrow_right.png)',
            className: 'plotline-left',
            radius: 10,
            align: 'right'
          },
          dataLabels: {
            align: 'right',
            enabled: true,
            useHTML: true,
            formatter() {
              return `<div class="plotline-right-label">${moment(this.x).format('HH:mm')}</div>`;
            }
          }
        } as Highcharts.PointOptionsObject,
        {
          noTooltip: true,
          x: moment.utc(setupEndTime).valueOf(),
          y: 0,
          groupId: 'ed',
          marker: {
            enabled: false,
            radius: 0
          }
        } as Highcharts.PointOptionsObject
      ],
      lineWidth: 3,
      showInLegend: false,
      color: '#00325C',
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          opacity: 1
        }
      }
    };

    const pointEditAnnotationSeries: Highcharts.SeriesLineOptions = {
      id: 'pointEditAnnotationSeries',
      name: 'splitDownTimeEndSeries',
      type: 'line',
      stickyTracking: false,
      dragDrop: {
        draggableX: true,
        groupBy: 'groupId',
        dragMinX: editMinTime ? moment.utc(editMinTime).valueOf() : undefined,
        dragMaxX: editMaxTime ? moment.utc(editMaxTime).valueOf() : undefined
      },
      data: [
        {
          x: moment.utc(setupEndTime).valueOf(),
          y: maxSpeedAxis1,
          groupId: 'sd',
          marker: {
            enabled: true,
            fillColor: '#00325C',
            radius: 0
          },
          noTooltip: true,
          dataLabels: {
            enabled: true,
            align: 'left',
            useHTML: true,
            formatter() {
              return (
                '<img src="/assets/images/phase-setup_indicator.png" class="flag-indicator"> </img>' +
                `<div class="down-time">${moment(this.x).format('HH:mm')}</div>`
              );
            }
          }
        } as Highcharts.PointOptionsObject,
        {
          x: moment.utc(setupEndTime).valueOf(),
          y: 0,
          noTooltip: true,
          groupId: 'sd',
          marker: {
            enabled: true,
            cursor: 'Pointer',
            symbol: 'url(/assets/images/phase-setup_circle.png)',
            radius: 10
          }
        } as Highcharts.PointOptionsObject
      ],
      color: '#00325C',
      turboThreshold: this.turboThreshold,
      lineWidth: 3,
      showInLegend: false,
      states: {
        inactive: {
          enabled: false,
          opacity: 1
        }
      }
    };

    const productionOrderSeries = this.createProductionOrderSeries(result, maxSpeedAxis1, displayWindow);

    const targetSpeedSeries = this.createTargetSpeedSeries(result, isMarkerEnabled, displayWindow);

    const speed2Series = this.createSpeed2Series(allDataSignal, isMarkerEnabled, smoothingAlgorithm, windowSize);

    const washSignalSeries = this.createWashSignalSeries(allDataSignal, setupDowntimeSeriesHeight, isMarkerEnabled);
    availableSignalSeries.push(washSignalSeries);

    const downTimeMarkerSeries = this.createDowntimeMarkerSeries(downtimeResult, maxSpeedAxis1, displayWindow);

    const wasteAssignmentSeries = this.createWasteAssignmentSeries(wasteReasons, maxSpeedAxis1);

    const shiftSeries = this.createShiftSeries(result, maxSpeedAxis1);

    result.productionPeriodData.forEach((status) => {
      const periodStart = moment.utc(this.clamp(status.periodStart, displayWindow)).local().valueOf();
      const periodEnd = moment.utc(this.clamp(status.periodEnd, displayWindow)).local().valueOf();

      if (status.workCenterStatus === 'Setup') {
        setupSeries.data.push({
          x: periodStart,
          y: 0,
          ['status']: status,
        } as PointOptionsObject);
        setupSeries.data.push({
          x: periodStart,
          y: setupDowntimeSeriesHeight,
          ['status']: status,
        } as PointOptionsObject);

        setupSeries.data.push({
          x: periodEnd,
          y: setupDowntimeSeriesHeight,
          ['status']: status,
        } as PointOptionsObject);
        setupSeries.data.push({
          x: periodEnd,
          y: 0,
          ['status']: status,
        } as PointOptionsObject);
        setupSeries.data.push({
          x: periodEnd,
          y: null,
        } as PointOptionsObject);
      } else if (status.downtimeData) {
        downTimeSeries.data.push({
          x: periodStart,
          y: 0,
          externalDowntimeLocalCode: '',
          ['status']: status,
        } as PointOptionsObject);

        downTimeSeries.data.push({
          x: periodStart,
          y: setupDowntimeSeriesHeight,
          externalDowntimeLocalCode: this.emptyTextPlaceholderPipe.transform(status.downtimeData.externalDowntimeLocalCode),
          ['status']: status,
        } as PointOptionsObject);

        downTimeSeries.data.push({
          x: periodEnd,
          y: setupDowntimeSeriesHeight,
          externalDowntimeLocalCode: '',
          ['status']: status,
        } as PointOptionsObject);

        downTimeSeries.data.push({
          x: periodEnd,
          y: 0,
          externalDowntimeLocalCode: '',
          ['status']: status,
        } as PointOptionsObject);

        downTimeSeries.data.push({
          x: periodEnd,
          y: null,
          externalDowntimeLocalCode: '',
        } as PointOptionsObject);
      }
    });

    plotBandSeries.data.push([moment.utc(setupStartTime).local().valueOf(), maxSpeedAxis1]);
    plotBandSeries.data.push([moment.utc(setupEndTime).local().valueOf(), maxSpeedAxis1]);

    plotBandSeries.data.push([moment.utc(setupEndTime).local().valueOf(), 0]);
    plotBandSeries.data.push([moment.utc(setupStartTime).local().valueOf(), 0]);

    allDataSignal.forEach((signalData) => {
      if (
        signalData.telemetryQuery.wellKnownSignal === SignalType.counter_qty ||
        signalData.telemetryQuery.wellKnownSignal === SignalType.speed2 ||
        signalData.telemetryQuery.wellKnownSignal === SignalType.wash
      ) {
        // Skip signal data, it was handled outside this loop
      } else if (signalData.telemetryQuery.wellKnownSignal === SignalType.speed) {
        const result = FilterAlgorithmService.speedGapFilter(signalData.telemetryData, displayWindow);

        result.errorSeries.forEach((errorSignalData) => {
          errorSeries.data.push({
            x: moment(errorSignalData.timestamp).valueOf(),
            y: errorSignalData.value * errorSeriesHeight,
            ['errorSignalData']: errorSignalData,
          } as PointOptionsObject);
        });

        this.smoothSignal(result.speedSeries, smoothingAlgorithm, windowSize).forEach((speedSignalData) => {
            speedSeries.data.push({
              x: moment(speedSignalData.timestamp).valueOf(),
              y: speedSignalData.value,
            } as PointOptionsObject);
          });
      } else if (signalData.telemetryQuery.wellKnownSignal === SignalType.machine_run) {
        if (signalData.telemetryData.length > 0) {
          signalData.telemetryData.forEach((runSignalData) => {
            if (runSignalData.value > 0) {
              machineRunSeries.data.push({
                x: moment(runSignalData.timestamp).local().valueOf(),
                y: runSeriesHeight,
              } as PointOptionsObject);
            }
          });
        }
      } else {
        const otherThis = this;
        const newSeries = {
          id: this.getNewSeriesName(signalData.telemetryQuery.wellKnownSignal),
          name: this.getNewSeriesName(signalData.telemetryQuery.wellKnownSignal),
          data: [] as any[], // Explicitly type as any[] to allow custom properties
          type: 'line',
          showInLegend: true,
          lineColor: '#393d37',
          step: 'left',
          color: '#393d37',
          turboThreshold: this.turboThreshold,
          inside: true,
          states: {
            inactive: {
              opacity: 1
            }
          },
          tooltip: {
            headerFormat: '',
            pointFormatter() {
              const wellKnownSignal = this['wellKnownSignal'];
              if (!wellKnownSignal) {
                return '';
              }
              return `<div class='generaltooltip'> ● ${wellKnownSignal}<br>
                ${otherThis.formatDateTime(this.x)}<br>
                ${otherThis.decimalPipe.transform(this.y?.toFixed(2))}
              </div>`;
            }
          }
        };
        if (signalData.telemetryData.length > 0) {
          signalData.telemetryData.forEach((newSeriesData) => {
            (newSeries.data as any[]).push({
              x: moment(newSeriesData.timestamp).local().valueOf(),
              y: Math.max(0, newSeriesData.value),
              ['wellKnownSignal']: signalData.telemetryQuery.wellKnownSignal,
            } as PointOptionsObject);
          });
          availableSignalSeries.push(newSeries);
        }
      }
    });

    const availableSignalSeriesSorted = availableSignalSeries.sort((a, b) => (a.name > b.name ? 1 : -1));

    switch (chartMode) {
      case ChartType.fullView:
        return [
          errorSeries,
          setupSeries,
          downTimeSeries,
          speedSeries,
          speed2Series,
          ...availableSignalSeriesSorted,
          machineRunSeries,
          shiftSeries,
          productionOrderSeries,
          targetSpeedSeries,
          wasteAssignmentSeries
        ];
      case ChartType.downtimeTask:
        return [errorSeries, speedSeries, setupSeries, downTimeSeries, machineRunSeries, downTimeMarkerSeries];
      case ChartType.finishPhase:
        return [errorSeries, speedSeries, setupSeries, downTimeSeries, machineRunSeries];
      case ChartType.editSetup:
        return [
          errorSeries,
          speedSeries,
          setupSeries,
          downTimeSeries,
          machineRunSeries,
          plotBandSeries,
          rangeEditStartSeries,
          rangeEditEndSeries
        ];
      case ChartType.splitDowntime:
        return [errorSeries, speedSeries, setupSeries, downTimeSeries, machineRunSeries, pointEditAnnotationSeries];
      case ChartType.editDowntime:
        return [
          errorSeries,
          speedSeries,
          setupSeries,
          downTimeSeries,
          machineRunSeries,
          plotBandSeries,
          rangeEditStartSeries,
          editDowntimeEndSeries
        ];
      default:
        return [];
    }
  }

  private createShiftSeries(result: MachineChartData, maxSpeedAxis1: number) {
    const otherThis = this;
    const shiftSeries: Highcharts.SeriesLineOptions = {
      id: 'shiftSeries',
      name: this.shiftText,
      type: 'line',
      dashStyle: 'Solid',
      showInLegend: false,
      color: '#000000',
      lineWidth: 3,
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          enabled: false,
          opacity: 1
        }
      },
      data: [],
      dataLabels: [
        {
          enabled: true,
          align: 'right',
          verticalAlign: 'bottom',
          format: '{point.shiftName}',
          style: {
            textShadow: 'none',
            fontSize: '14px'
          },
          rotation: -90,
          x: 10
        }
      ],
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          const status = this['status'];
          if (!status) {
            return '';
          }
          return `<div class='generaltooltip'> ● ${otherThis.shiftText}: <b>${status.shiftTypeName}</b><br>
            ${otherThis.externalShiftTypeText}: <b>${status.externalShiftTypeId}</b><br>
            ${otherThis.dateText}: <b>${otherThis.formatDateTime(status.shiftStartTime)}</b>
          </div>`;
        }
      }
    };

    result.shiftData.forEach((status) => {
      const shiftStartTime = moment.utc(status.shiftStartTime).local().valueOf();
      shiftSeries.data.push({
        x: shiftStartTime,
        y: 0,
        ['status']: status,
      } as PointOptionsObject);
      shiftSeries.data.push({
        x: shiftStartTime,
        y: maxSpeedAxis1,
        ['shiftName']: status.shiftTypeName,
        ['status']: status,
      } as PointOptionsObject);
      shiftSeries.data.push({
        x: shiftStartTime,
        y: 0,
        ['status']: status,
      } as PointOptionsObject);
      shiftSeries.data.push({
        x: shiftStartTime,
        y: null,
        ['status']: status,
      } as PointOptionsObject);
    });

    return shiftSeries;
  }

  public newSeriesNames = (): Observable<any> => this.translate.get(['MACHINE_CHART.PRODUCTION_CHANGE_SERIES']);

  private createProductionOrderSeries(
    result: MachineChartData,
    maxSpeedAxis1: number,
    displayWindow: DisplayWindow
  ): Highcharts.SeriesLineOptions
  {
    const outerThis = this;
    const productionOrderSeries: Highcharts.SeriesLineOptions = {
      id: 'productionOrderSeries',
      name: 'ProductionOrderSeries',
      type: 'line',
      dashStyle: 'Solid',
      showInLegend: false,
      color: '#000000',
      lineWidth: 2,
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          enabled: false,
          opacity: 1
        }
      },
      data: [],
      dataLabels: [
        {
          enabled: true,
          align: 'right',
          verticalAlign: 'bottom',
          format: '{point.externalProductionOrderId}',
          style: {
            textShadow: 'none',
            fontSize: '14px'
          },
          rotation: -90,
          x: 20,
          y: 160
        }
      ],
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          const productionOrder = this['productionOrder'];
          if (!productionOrder) {
            return '';
          }
          return outerThis.getProductionOrderTooltipText(productionOrder);
        }
      }
    };

    // add production order change points
    result.productionOrderData.forEach((productionOrder) => {
      if (productionOrder.externalProductionOrderId !== null) {
        const runStartTime = moment.utc(this.clamp(productionOrder.runStartTime, displayWindow)).local().valueOf();

        productionOrderSeries.data.push({
          x: runStartTime,
          y: 0,
          ['productionOrder']: productionOrder,
        } as PointOptionsObject);
        productionOrderSeries.data.push({
          x: runStartTime,
          y: maxSpeedAxis1,
          ['productionOrder']: productionOrder,
          ['externalProductionOrderId']: productionOrder.externalProductionOrderId,
        } as PointOptionsObject);
        productionOrderSeries.data.push({
          x: runStartTime,
          y: null,
          ['productionOrder']: productionOrder,
        } as PointOptionsObject);
      }
    });

    return productionOrderSeries;
  }

  private createTargetSpeedSeries(
    result: MachineChartData,
    isMarkerEnabled: boolean,
    displayWindow: DisplayWindow): Highcharts.SeriesLineOptions
  {
    const outerThis = this;
    const targetSpeedSeries: Highcharts.SeriesLineOptions = {
      id: 'targetSpeedSeries',
      name: this.targetSpeedText,
      type: 'line',
      data: [],
      dashStyle: 'Dash',
      color: '#5E5E5E',
      lineWidth: 2,
      step: 'left',
      turboThreshold: this.turboThreshold,
      showInLegend: true,
      marker: {
        enabled: isMarkerEnabled
      },
      states: {
        inactive: {
          opacity: 1
        }
      },
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          const status = this['status'];
          if (!status) {
            return '';
          }
          return `<div class='generaltooltip'> ● ${outerThis.targetSpeedText} <br>
            ${status.externalProductionOrderId}<br>
            ${moment.utc(status.runStartTime).local().format('HH:mm:ss')}<br>
            ${outerThis.formatQuantityPipe.transform(status.targetSpeed)}
          </div>`;
        }
      }
    };

    targetSpeedSeries.showInLegend = result.productionOrderData.length > 0;

    result.productionOrderData.forEach((status) => {
      const runStartTime = moment.utc(this.clamp(status.runStartTime, displayWindow)).local().valueOf();
      const runEndTime = moment.utc(this.clamp(status.runEndTime, displayWindow)).local().valueOf();

      targetSpeedSeries.data.push({
        x: runStartTime,
        y: status.targetSpeed.value,
        ['status']: status,
      } as any);

      targetSpeedSeries.data.push({
        x: runEndTime,
        y: status.targetSpeed.value,
        ['status']: status,
      } as any);

      // draw the end vertical line when the run end is before the display window end
      if (runEndTime !== moment.utc(displayWindow.end).local().valueOf()) {
        targetSpeedSeries.data.push({
          x: runEndTime,
          y: 0,
        });
        targetSpeedSeries.data.push({
          x: runEndTime,
          y: null,
        });
      }
    });

    return targetSpeedSeries;
  }

  private createSpeed2Series(
    allDataSignal: TelemetryResultDto[],
    isMarkerEnabled: boolean,
    smoothingAlgorithm: string,
    windowSize: number
  ): Highcharts.SeriesOptionsType {
    const otherThis = this;
    const speed2Series: Highcharts.SeriesLineOptions = {
      id: 'speed2Series',
      name: this.speed2SeriesText,
      type: 'line',
      data: [],
      turboThreshold: this.turboThreshold,
      step: 'left',
      color: '#393d37',
      states: {
        inactive: {
          opacity: 1
        }
      },
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          const wellKnownSignal = this['wellKnownSignal'];
          if (!wellKnownSignal) {
            return '';
          }
          return `<div class='generaltooltip'> ● ${wellKnownSignal}<br>
            ${otherThis.formatDateTime(this.x)}<br>
            ${otherThis.decimalPipe.transform(this.y?.toFixed(2))}
          </div>`;
        }
      },
      marker: {
        enabled: isMarkerEnabled
      },
      yAxis: 1
    };

    const signalDataFiltered = allDataSignal.filter((signalData) => signalData.telemetryQuery.wellKnownSignal === SignalType.speed2);

    speed2Series.showInLegend = this.hasSignal(signalDataFiltered);

    if (signalDataFiltered.length > 0) {
      const signalData = signalDataFiltered[0];
      this.smoothSignal(signalData.telemetryData, smoothingAlgorithm, windowSize).forEach((speed2Data) => {
        speed2Series.data.push({
          x: moment.utc(speed2Data.timestamp).local().valueOf(),
          y: Math.max(0, speed2Data.value),
          ['wellKnownSignal']: signalData.telemetryQuery.wellKnownSignal,
        } as any);
      });
    }

    return speed2Series;
  }

  private createWashSignalSeries(
    allDataSignal: TelemetryResultDto[],
    setupDownLimit,
    isMarkerEnabled: boolean
  ): Highcharts.SeriesAreaOptions {
    const otherThis = this;
    const washSignalSeries: Highcharts.SeriesAreaOptions = {
      id: 'washSignalSeries',
      name: this.washSeriesText,
      type: 'area',
      data: [],
      step: 'left',
      color: '#7794AF',
      fillColor: '#7794AFCC',
      turboThreshold: this.turboThreshold,
      states: {
        inactive: {
          opacity: 1
        }
      },
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          const wellKnownSignal = this['wellKnownSignal'];
          if (!wellKnownSignal) {
            return '';
          }
          return `<div class='generaltooltip'> ● ${wellKnownSignal}<br>
              ${otherThis.formatDateTime(this.x)}
            </div>`;
        }
      },
      marker: {
        enabled: isMarkerEnabled
      },
      yAxis: 0
    };

    const signalDataFiltered = allDataSignal.filter((signalData) => signalData.telemetryQuery.wellKnownSignal === SignalType.wash);

    washSignalSeries.showInLegend = this.hasSignal(signalDataFiltered);

    if (signalDataFiltered.length > 0) {
      const signalData = signalDataFiltered[0];
      if (signalData.telemetryData.length > 0) {
        signalData.telemetryData.forEach((washSignal) => {
          washSignalSeries.data.push({
            x: moment.utc(washSignal.timestamp).local().valueOf(),
            y: washSignal.value === 1 ? setupDownLimit : 0,
            ['wellKnownSignal']: signalData.telemetryQuery.wellKnownSignal,
          } as any);
        });
      }
    }

    return washSignalSeries;
  }

  private createDowntimeMarkerSeries(
    downtimeResult: WorkCenterTask,
    maxSpeedAxis1: number,
    displayWindow: DisplayWindow): Highcharts.SeriesAreaOptions {
    const downTimeMarkerSeries: Highcharts.SeriesAreaOptions = {
      id: 'downTimeMarkerSeries',
      name: this.downModeText,
      type: 'area',
      data: [],
      color: '#00325C',
      lineColor: '#00325C',
      showInLegend: false,
      enableMouseTracking: false,
      turboThreshold: this.turboThreshold,
      fillColor: {
        pattern: {
          path: {
            d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
            strokeWidth: 1
          },
          width: 10,
          height: 10,
          opacity: 0.4
        }
      },
      fillOpacity: 0.8,
      marker: {
        enabled: true,
        fillColor: '#00325C',
        symbol: 'circle'
      },
      tooltip: {},
      dataLabels: {
        enabled: true,
        backgroundColor: '#00325C',
        shape: 'flags',
        style: {
          color: 'white',
          fontWeight: 'bold',
          textShadow: 'none'
        },
        useHTML: true,
        formatter() {
          return `<span style="padding: 5px;">${moment(this.x).format('HH:mm')}</span>`;
        }
      },
      states: {
        inactive: {
          opacity: 1
        }
      }
    };

    if (ChartType.downtimeTask && downtimeResult !== null && downtimeResult !== undefined) {
      downTimeMarkerSeries.data.push([
        moment.utc(this.clamp(downtimeResult.downtime.startTime, displayWindow)).local().valueOf(),
        maxSpeedAxis1]);
      downTimeMarkerSeries.data.push([
        moment.utc(this.clamp(downtimeResult.downtime.endTime, displayWindow)).local().valueOf(),
        maxSpeedAxis1
      ]);
    }

    return downTimeMarkerSeries;
  }

  private createWasteAssignmentSeries(wasteReasons: WasteAssignmentInTime[], maxSpeedAxis1: number): Highcharts.SeriesOptionsType {
    const outerThis = this;
    const wasteAssignmentSeries: Highcharts.SeriesLineOptions = {
      id: 'wasteAssignmentSeries',
      name: 'Waste',
      type: 'line',
      yAxis: 0,
      color: 'red',
      lineWidth: 0,
      data: [],
      turboThreshold: this.turboThreshold,
      marker: {
        enabled: true,
        radius: 5,
        symbol: 'circle'
      },
      states: {
        hover: {
          lineWidthPlus: 0
        }
      },
      tooltip: {
        headerFormat: '',
        pointFormatter() {
          return outerThis.wasteTooltip(this['wasteData']);
        }
      }
    };

    wasteReasons.forEach((wasteData) => {
      wasteAssignmentSeries.data.push({
        x: moment.utc(wasteData.timeStamp).local().valueOf(),
        y: 0.025 * maxSpeedAxis1, // waste series marker position on y-axis.
        ['wasteData']: wasteData,
      } as any);
    });

    return wasteAssignmentSeries;
  }

  private wasteTooltip(wasteAssignment: WasteAssignmentInTime): string {
    return `<div class="wastetooltip">
        <div class='generaltooltip'> ● ${this.translate.instant('MACHINE_CHART.WASTE_ASSIGNMENT')}<br>
          ${this.formatDateTime(wasteAssignment.timeStamp)}
          ${this.wasteReasons(wasteAssignment?.wastesPerReason)}<br>
          ${this.getMaculatureText(wasteAssignment)}
        </div>
      </div>`;
  }

  private getMaculatureText(wasteAssignment: WasteAssignmentInTime) {
    if (wasteAssignment?.maculature?.value > 0) {
      return `
         ${this.translate.instant('MACHINE_CHART.MACULATURE')}: <b>
         ${this.formatQuantityPipe.transform(wasteAssignment.maculature)}</b>`;
    } else {
      return '';
    }
  }

  private wasteReasons(wastes: WastePerReason[]): string {
    let text = '';
    wastes.forEach((element) => {
      text += `<br>
      ${this.translate.instant('MACHINE_CHART.WASTE')}: <b>${this.formatQuantityPipe.transform(element.waste)}</b><br>
      ${this.translate.instant('MACHINE_CHART.WASTE_REASON')}: <b>${element.externalWasteReasonCode} ${element.description}</b>`;
    });

    return text;
  }

  public getNewSeriesName(seriesName: string): string {
    this.newSeriesNames().subscribe((result) => {
      Object.keys(result).map((key) => {
        switch (key) {
          case 'MACHINE_CHART.PRODUCTION_CHANGE_SERIES':
            this.productionChangeSeries = result[key];
            break;
          case 'MACHINE_CHART.NONE':
            this.productionChangeSeries = result[key];
            break;
        }
      });
      switch (seriesName) {
        case 'production_change':
          this.newSeriesName = this.productionChangeSeries;
          break;
        default:
          this.newSeriesName = seriesName;
          break;
      }
    });
    return this.newSeriesName ?? seriesName;
  }

  public getProductionOrderTooltipText(productionOrder: ProductionOrderData2): string {
    if (!productionOrder) {
      return '';
    }
    return `<div class='generaltooltip'> ● <b>${productionOrder.externalProductionOrderId}</b><br>
  ${this.finishGoodArticleNumberText}: <b>${productionOrder.finishedGoodArticleId}</b><br>
  ${this.finishGoodArticleNameText}: <b>${productionOrder.finishedGoodArticleName}</b><br>
  ${this.configNameNumberText}: <b> ${productionOrder.finishedGoodConfigurationId} ${productionOrder.finishedGoodConfigurationName}</b><br>
  ${this.goodQuantityUnitText}: <b>${this.formatQuantityPipe.transform(productionOrder.goodQuantity, false)} /
  ${this.formatQuantityPipe.transform(productionOrder.targetQuantity)} </b><br>
  ${this.wasteQuantMalQuntText}: <b>${this.formatQuantityPipe.transform(productionOrder.orderWasteQuantity)},
  ${this.formatQuantityPipe.transform(productionOrder.orderMaculatureQuantity)}</b>
</div>`;
  }

  public getDowntimeTooltipText(status: ProductionPeriodDetailData | null): string {
    if (!status) {
      return '';
    }
    return `<div class='downtimetooltip'> ● ${this.downModeText} <br>
            ${this.formatDateTime(status.periodStart)}<br>
            ${this.downtimeCodeText}: <b>${this.emptyTextPlaceholderPipe.transform(status.downtimeData?.externalDowntimeLocalCode)}</b><br>
            ${this.downtimeCommentText}: <b>${status.downtimeData?.downtimeComment ?? this.noCommentText}</b>
            ${this.getDowntimeWasteHTMLString(status)}
          </div>`;
  }

  public getSetupPhaseTooltipText(status: ProductionPeriodDetailData | null): string {
    if (!status) {
      return '';
    }
    return `<div class='setuptooltip'> ● ${this.setupModeText} <br>
            ${this.formatDateTime(status.periodEnd)}<br>
            ${this.setupKindText}: <b>
              ${this.emptyTextPlaceholderPipe.transform(status.setupData?.externalSetupParameterKindId) }
              ${this.emptyTextPlaceholderPipe.transform(status.setupData?.setupParameterKindDescription) }</b>
            ${this.getSetupWasteHtmlString(status)}
          </div>`;
  }

  public getSetupWasteHtmlString(status: ProductionPeriodDetailData | null): string {
    let setupWaste = '';
    if (!status || !status.setupData || !status.setupData.setupPhaseWaste) {
      return;
    }

    const wasteInfo = status.setupData.setupPhaseWaste;

    if (wasteInfo.wastePerReason && wasteInfo.wastePerReason.length > 0) {
      wasteInfo.wastePerReason.map((item) => {
        if (item.waste && item.waste?.value > 0) {
          setupWaste += `<br>${this.wasteQuantityText}: <b>${this.formatQuantityPipe.transform(item.waste)}</b>`;
        }
        if (item.externalWasteReasonCode && item.externalWasteReasonCode.trim().length > 0) {
          setupWaste += `<br>${this.wasteReasonText}: <b>${item.externalWasteReasonCode} ${item.description}</b>`;
        }
      });
    }

    if (wasteInfo.quantity && wasteInfo.quantity.value > 0) {
      setupWaste += `<br>${this.maculatureQuantityText}: <b> ${this.formatQuantityPipe.transform(wasteInfo.quantity)}</b>`;
    }

    return setupWaste;
  }

  public getErrorSeriesTooltipText(errorSignalData: TelemetryValueDto | null): string {
    if (!errorSignalData) {
      return '';
    }
    return errorSignalData.value
      ? '<div class="generaltooltip">' +
          `● ${this.errorSeriesText}<br>` +
          `${this.formatDateTime(errorSignalData.timestamp)}</div>`
      : null;
  }

  public getDowntimeWasteHTMLString(status: ProductionPeriodDetailData | null): string {
    let downTimeWaste = '';

    if (!status || !status.downtimeData?.downtimeWaste) {
      return downTimeWaste;
    }

    const wasteInfo = status.downtimeData.downtimeWaste;

    if (wasteInfo.wastePerReason && wasteInfo.wastePerReason.length > 0) {
      wasteInfo.wastePerReason.map((item) => {
        if (item.waste && item.waste?.value > 0) {
          downTimeWaste += `<br>${this.wasteQuantityText}: <b>${this.formatQuantityPipe.transform(item.waste)}</b>`;
        }
        if (item.externalWasteReasonCode && item.externalWasteReasonCode.trim().length > 0) {
          downTimeWaste += `<br>${this.wasteReasonText}: <b>${item.externalWasteReasonCode} ${item.description}</b>`;
        }
      });
    }
    if (wasteInfo.quantity && wasteInfo.quantity.value > 0) {
      downTimeWaste += `<br>${this.maculatureQuantityText}: <b>${this.formatQuantityPipe.transform(wasteInfo.quantity)}</b>`;
    }
    return downTimeWaste;
  }

  private getGenericTooltipText(outerThis: MachineChartConfigurationService, point: Point, showValue: boolean): string {
    if (showValue) {
      return `<div class='generaltooltip'> ● ${outerThis.runModeText}<br>
                ${outerThis.formatDateTime(point.x)}<br>
                ${outerThis.emptyTextPlaceholderPipe.transform(outerThis.decimalPipe.transform(point.y?.toFixed(0)))}
              </div>`;
    } else {
      return `<div class='generaltooltip'> ● ${outerThis.runModeText}<br>
              ${outerThis.formatDateTime(point.x)}
            </div>`;
    }
  }

  public getSeriesTitleText(counterUnitId: string): string {
    if (counterUnitId === 'SHT') {
      return this.shtCounterText;
    }
    if (counterUnitId === 'PCS') {
      return this.pcsCounterText;
    }
    return counterUnitId;
  }

  private formatDateTime(dateTimeText: string | number | null): string {
    if (!dateTimeText) {
      return '–';
    }
    return moment.utc(dateTimeText).local().format('DD.MM.yyyy HH:mm:ss');
  }

  private hasSignal(signalDataFiltered: TelemetryResultDto[]): boolean {
    return signalDataFiltered.length > 0 && signalDataFiltered[0].telemetryData.length > 0;
  }

  private smoothSignal(signal: TelemetryValueDto[], smoothingAlgorithm: string, windowSize: number): TelemetryValueDto[] {
    console.info('Applying Smoothing algorithm: ', smoothingAlgorithm, ' with window size: ', windowSize);
    switch (smoothingAlgorithm) {
      case 'MovingAverage':
        return FilterAlgorithmService.movingAverageFilter(signal, windowSize);
      case 'SavitzkyGolay':
        return FilterAlgorithmService.savitzkyGolay(signal, windowSize, 2);
      case 'ReadingAnomaly':
        return FilterAlgorithmService.readingAnomalyFilter(signal);
      case 'Median':
        return FilterAlgorithmService.medianFilter(signal, windowSize);
      case 'Loess':
        return FilterAlgorithmService.loessFilter(signal, windowSize);
      case 'none':
      default:
        return signal;
    }
  }

  private clamp(dateTime: string | number | null, displayWindow: DisplayWindow): number {
    if (!dateTime) {
      return moment.utc(displayWindow.end).valueOf();
    }

    const clamped = Math.min(
      Math.max(
        moment.utc(dateTime).valueOf(),
        moment.utc(displayWindow.start).valueOf()),
      moment.utc(displayWindow.end).valueOf());

    return clamped;
  }
}
